import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, ArrowDownToLine, ArrowUpFromLine, TriangleAlert, Ban,
  Warehouse as WarehouseIcon, ClipboardList, PackageCheck, TrendingUp, Clock, Layers,
  PackageX,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Cell, LabelList,
} from 'recharts';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { useAuthStore } from '../../store/auth';
import { useBadges } from '../../hooks/useBadges';
import { brand } from '../../assets/brand';
import { Card, Tag, Skeleton, StatCard, EntityAvatar, entityHue, cn } from '../../ui';
import { orderStatusLabel } from '../../lib/statusTone';
import { RoleBadge, roleLabel } from '../../components/RoleBadge';
import { formatIst } from '../../lib/time';
import { CountUp, Stagger } from '../../ui/motion';
import { fmtQty, inr, variantText } from '../../lib/format';
import { PALETTE, neutralColor, seriesColorsFor } from '../../lib/palette';
import { useThemeStore } from '../../store/theme';

// Fixed per-status hues (do NOT change with the theme — they carry meaning). Chrome (bg/text/border)
// is themed via CSS vars so the charts still flip light/dark; only these data colours stay constant.
const STATUS_COLOR: Record<string, string> = {
  APPROVED: PALETTE.sky[500], ACCEPTED: PALETTE.plum[500], AWAITING_TRANSFER: PALETTE.warning[600],
  PACKED: PALETTE.harvest[400], DISPATCHED: PALETTE.brand[500], DISPATCHED_READY: PALETTE.harvest[400], DELIVERED: PALETTE.brand[600],
};
const ORDER_TONE: Record<string, any> = {
  APPROVED: 'blue', ACCEPTED: 'purple', PACKED: 'gold', DISPATCHED: 'green', AWAITING_TRANSFER: 'orange',
};
/*
 * The fulfilment funnel a WHM actually works, in the order the work moves — and ONLY the stages that
 * are still work. DISPATCHED and DELIVERED used to be plotted here too, under a card headed "open
 * orders by stage", which is a contradiction: a delivered order is not a stage anybody is standing
 * at, and the shipments card next to this one already owns what has left the building. Each stage
 * carries what it MEANS on the floor, because "Accepted" is a database word and "being picked and
 * packed" is what the reader is actually looking at.
 */
const PIPELINE: { status: string; label: string; note: string }[] = [
  { status: 'APPROVED', label: 'Approved', note: 'waiting to be picked' },
  { status: 'ACCEPTED', label: 'In fulfillment', note: 'being picked and packed' },
  { status: 'PACKED', label: 'Packed', note: 'boxed, waiting for a vehicle' },
];
/* Not a stage the warehouse can work: the stock is not there yet. Counted and named separately so it
   never silently inflates (or hides inside) the queue the tile above promises. */
const BLOCKED_STAGE = { status: 'AWAITING_TRANSFER', label: 'Awaiting transfer' };

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** How far a low-stock row is UNDER its minimum — the same `min - available` the alerts register
 *  sorts by and prints as "Short by", so the chart and the list agree on which breach is worst. */
const shortOf = (r: any) => Math.max(0, Number(r?.minLevel ?? 0) - Number(r?.available ?? 0));

/**
 * Warehouse Manager home. Everything here is scoped server-side to the warehouse(s) the manager is
 * assigned to (WarehouseScopeService), composed from the endpoints a WHM is allowed to call:
 * /warehouses/my, /badges, /dispatches, /stock/alerts/low-stock, /stock/non-sellable, /orders,
 * /stock-transfers. It surfaces the manager's daily actions (fulfil orders, receive/dispatch stock,
 * watch low / non-sellable stock) plus four charts — orders-received trend, the shipment queue,
 * fulfilment pipeline and the low-stock gap — all for their warehouse only.
 */
export function WhmDashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const badges = useBadges();
  // Charts draw to SVG but take literal colours, so the neutral greys have to be resolved in JS.
  const isDark = useThemeStore((s) => s.resolved)() === 'dark';
  // "+N more" is a real control, not a tooltip: it opens the rest of the strip in place.
  const [allSites, setAllSites] = useState(false);

  /*
   * The role pill beside the greeting, EXCEPT when it would stutter.
   *
   * This page is reachable by a warehouse manager, an admin and a super admin, and what it shows is
   * scoped differently for each, so which of them is reading is a fact about the screen. But several
   * accounts are NAMED for their role ("Super Admin", "ZZ-TEST Admin"), and on those the line came
   * out as "Hi, Super Admin · Super Admin" — the badge saying nothing the name had not.
   */
  const showRole = !!user?.role && !(user.fullName ?? '').toUpperCase().includes(roleLabel(user.role).toUpperCase());

  // Last 14 whole days (local time) for the received-orders trend.
  const range = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 13);
    return { from: ymd(start), to: ymd(end) };
  }, []);

  const { data: warehouses, isLoading: whLoading } = useQuery({ queryKey: ['whm-warehouses'], queryFn: () => request<any[]>({ url: '/warehouses/my' }) });
  const { data: dispatches } = useQuery({ queryKey: ['whm-dispatches'], queryFn: () => request<any>({ url: '/dispatches' }) });
  const { data: lowStock, isLoading: lowLoading } = useQuery({ queryKey: ['whm-low-stock'], queryFn: () => request<any[]>({ url: '/stock/alerts/low-stock' }) });
  const { data: nonSellable } = useQuery({ queryKey: ['whm-nonsellable'], queryFn: () => request<any>({ url: '/stock/non-sellable' }) });
  // Open orders (CLOSED hidden by default) → pipeline + the "to fulfil" queue.
  const { data: ordersRes, isLoading: ordLoading } = useQuery({ queryKey: ['whm-orders'], queryFn: () => requestAllPages('/orders', { sort: '-orderDate' }) });
  // Wider date-bounded pull (incl. closed) → per-day received trend.
  const { data: trendRes } = useQuery({ queryKey: ['whm-orders-trend', range.from, range.to], queryFn: () => requestAllPages('/orders', { from: range.from, to: range.to, includeClosed: true, sort: '-orderDate' }) });
  /*
   * ALL transfers, not just the in-transit ones. The page showed an "Outgoing transfers" count with
   * no list anywhere — a queue of seven things to do and no way to see what they were. One
   * unfiltered call (the server takes 200) feeds both directions; `status` on this endpoint is a
   * single value, so the three pre-dispatch states cannot be asked for in one filtered call anyway.
   */
  const { data: transfers } = useQuery({ queryKey: ['whm-transfers'], queryFn: () => request<any[]>({ url: '/stock-transfers' }) });

  const whs = warehouses ?? [];
  const myWhIds = useMemo(() => new Set(whs.map((w: any) => String(w.id))), [whs]);
  const dispSummary = dispatches?.summary ?? {};
  const lows = lowStock ?? [];
  /*
   * Non-sellable stock is MONEY, not just units. "29 units" says nothing about whether to care;
   * the endpoint already returns the dealer-price value of the batches and the split by reason, and
   * "₹2,755 of stock · 29 expired" is the version somebody acts on. `totalValue` comes back null
   * for a viewer without valuation rights, so the money half is dropped rather than shown as zero.
   */
  const nsSummary = nonSellable?.summary ?? {};
  const nsUnits = Number(nsSummary.totalUnits ?? 0);
  const nsValue = nsSummary.totalValue == null ? null : Number(nsSummary.totalValue);
  const nsWhy = useMemo(() => {
    const parts: string[] = [];
    if (Number(nsSummary.expiredUnits ?? 0)) parts.push(`${fmtQty(nsSummary.expiredUnits)} expired`);
    if (Number(nsSummary.qcUnits ?? 0)) parts.push(`${fmtQty(nsSummary.qcUnits)} on QC hold`);
    if (Number(nsSummary.blockedUnits ?? 0)) parts.push(`${fmtQty(nsSummary.blockedUnits)} blocked`);
    return parts.join(' · ');
  }, [nsSummary]);
  /*
   * OLDEST FIRST. The orders endpoint is sorted `-orderDate`, so this queue arrived newest-first and
   * the card showed the seven orders that need attention LEAST while the ones rotting at the bottom
   * never appeared. A work queue is worked from the oldest end.
   */
  /* Ageing helper, declared before the first useMemo that calls it: a useMemo body runs during this
     render, so a `const` further down the component is still in its temporal dead zone. */
  const dayMs = 86400000;
  const ageDays = (d?: string | null) => (d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / dayMs)) : 0);

  const orderQueue = useMemo(
    () => (ordersRes?.data ?? [])
      .filter((o: any) => ['APPROVED', 'ACCEPTED', 'PACKED'].includes(o.status))
      .sort((a: any, b: any) => new Date(a.orderDate ?? 0).getTime() - new Date(b.orderDate ?? 0).getTime()),
    [ordersRes],
  );
  /*
   * WHAT a transfer actually is, not just its number.
   *
   * The row said "1 item(s) · 9 units" — which tells a storeman nothing he can act on. What he needs
   * before walking to the bay is WHICH product, WHERE it is coming from, and HOW LONG it has been on
   * the road: a transfer in transit for a fortnight is a lost pallet, and the payload carried
   * `dispatchedAt` and named `lines` all along.
   */
  const whName = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of whs) m.set(String(w.id), w.code as string);
    return m;
  }, [whs]);
  const sideOf = (t: any) => {
    const lines = t.lines ?? [];
    const first = lines[0];
    return {
      id: t.id,
      transferNo: t.transferNo as string,
      qty: Number(t.totalQty ?? 0),
      items: Number(t.itemCount ?? lines.length ?? 0),
      // One product named, the rest counted — a list of five SKUs in a dashboard row is unreadable.
      product: first ? String(first.productName ?? first.sku ?? '') : '',
      more: Math.max(0, lines.length - 1),
      orderRef: t.remarks && /GB\//.test(String(t.remarks)) ? String(t.remarks).replace(/^.*?(GB\/\S+).*$/, '$1') : null,
      status: t.status as string,
    };
  };
  const incoming = useMemo(() => (transfers ?? [])
    .filter((t: any) => t.status === 'IN_TRANSIT' && (myWhIds.size === 0 || myWhIds.has(String(t.toWarehouseId))))
    // `toWarehouseId` is kept on the mapped row because the per-warehouse table counts incoming by it.
    .map((t: any) => ({ ...sideOf(t), toWarehouseId: t.toWarehouseId, from: whName.get(String(t.fromWarehouseId)) ?? '—', days: ageDays(t.dispatchedAt ?? t.transferDate) }))
    .sort((a, b) => b.days - a.days), [transfers, myWhIds, whName]);
  // Everything the dispatch flow accepts before the stock leaves — the same three states the
  // "Outgoing transfers" badge counts (badges.module.ts), so the tile and this list agree.
  const outgoing = useMemo(() => (transfers ?? [])
    .filter((t: any) => ['REQUESTED', 'SOURCE_ACK', 'PREPARED'].includes(t.status) && (myWhIds.size === 0 || myWhIds.has(String(t.fromWarehouseId))))
    .map((t: any) => ({ ...sideOf(t), to: whName.get(String(t.toWarehouseId)) ?? '—', days: ageDays(t.transferDate) }))
    .sort((a, b) => b.days - a.days), [transfers, myWhIds, whName]);

  /*
   * AGEING. A queue count says how much is waiting; only its AGE says whether anything is wrong.
   * "97 orders to fulfil" reads the same on a good day and on a warehouse that has not shipped in a
   * fortnight — the oldest date is what separates them, and every payload already carried it.
   */
  /*
   * ONE definition of "orders to fulfil" for the whole page — the tile, the hero sentence, the
   * per-warehouse table and the list below all count the same rows.
   *
   * It deliberately no longer reads `badges.ordersActive`, which means two DIFFERENT things by role
   * (badges.module.ts): for a Warehouse Manager it is exactly APPROVED/ACCEPTED/PACKED on their
   * warehouses, but for everyone else it is every order NOT delivered/invoiced/closed/cancelled —
   * so an Admin saw 97 under a label that reads "approved · in fulfillment · packed", counting
   * PLACED, ON_HOLD and BACKORDER orders no warehouse can act on. The page's own rows said 28.
   *
   * The honest cost of counting client-side is the page cap, so the page SAYS when it is capped
   * rather than presenting a partial figure as a total.
   */
  const ordersCapped = useMemo(() => {
    const loaded = ordersRes?.data?.length ?? 0;
    return loaded > 0 && (ordersRes?.meta?.total ?? 0) > loaded;
  }, [ordersRes]);

  const queueAge = useMemo(() => {
    const ages = orderQueue.map((o: any) => ageDays(o.orderDate)).filter((n: number) => n >= 0);
    return { oldest: ages.length ? Math.max(...ages) : 0, stale: ages.filter((n: number) => n >= 7).length };
  }, [orderQueue]);
  const lowAge = useMemo(() => {
    const ages = lows.map((r: any) => ageDays(r.openedAt));
    return ages.length ? Math.max(...ages) : 0;
  }, [lows]);

  /*
   * WHERE the work sits.
   *
   * Every number on this page was one company-wide total. That is the right answer for a Warehouse
   * Manager, who has one site — but an Admin opening this sees ten warehouses summed into "97" with
   * nothing saying WHICH of them is behind, which is the only question a ten-site reader has. The
   * orders payload already carries `dispatchWarehouseId`, low stock carries `warehouseId` and
   * transfers carry `toWarehouseId`; nothing here needs a new endpoint.
   */
  const byWarehouse = useMemo(() => {
    if (whs.length < 2) return [];
    const rows = whs.map((w: any) => {
      const id = String(w.id);
      const mine = orderQueue.filter((o: any) => String(o.dispatchWarehouseId ?? '') === id);
      const oldest = mine.reduce((m: number, o: any) => Math.max(m, ageDays(o.orderDate)), 0);
      return {
        id,
        code: w.code as string,
        name: w.name as string,
        toFulfil: mine.length,
        packed: mine.filter((o: any) => o.status === 'PACKED').length,
        lowStock: lows.filter((r: any) => String(r.warehouseId) === id).length,
        incoming: incoming.filter((t: any) => String(t.toWarehouseId) === id).length,
        oldest,
      };
    });
    // Busiest first, and a site with nothing waiting is not a row worth scrolling past.
    return rows.filter((r) => r.toFulfil || r.lowStock || r.incoming)
      .sort((a, b) => (b.toFulfil - a.toFulfil) || (b.lowStock - a.lowStock));
  }, [whs, orderQueue, lows, incoming]);
  const whColors = useMemo(() => seriesColorsFor(byWarehouse.map((r) => r.code)), [byWarehouse]);
  const whMax = useMemo(() => byWarehouse.reduce((m, r) => Math.max(m, r.toFulfil), 0), [byWarehouse]);
  // Orders whose warehouse was never assigned belong to nobody's queue — name them rather than let
  // the per-warehouse rows quietly fail to add up to the headline.
  const unassigned = useMemo(
    () => (whs.length < 2 ? 0 : orderQueue.filter((o: any) => !o.dispatchWarehouseId).length),
    [whs, orderQueue],
  );

  // ── Chart data (derived client-side; no extra endpoints) ─────────────────────────
  const trendData = useMemo(() => {
    const days: { day: string; count: number }[] = [];
    const idx: Record<string, number> = {};
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const key = ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - i));
      idx[key] = days.length;
      days.push({ day: key, count: 0 });
    }
    for (const o of (trendRes?.data ?? [])) {
      // Bucket by the LOCAL/IST calendar day (same `ymd` basis as the keys above) — NOT the UTC slice of
      // the ISO string, which shifts an early-morning-IST order back a day (or out of the 14-day window).
      const k = o.orderDate ? ymd(new Date(o.orderDate)) : '';
      if (k in idx) days[idx[k]].count += 1;
    }
    return days;
  }, [trendRes]);
  const trendTotal = useMemo(() => trendData.reduce((a, b) => a + b.count, 0), [trendData]);

  /*
   * STAGES OF ONE QUEUE — a partition of the very orders the tile above counts, not five free-
   * standing bars.
   *
   * This was a bar chart: five bars, an axis, and a 200px void under it, answering "how many are at
   * each stage" and nothing else. The two questions a warehouse manager actually asks of a pipeline
   * are how the work is SPLIT (am I behind on picking, or on vans?) and how LONG the oldest thing at
   * each stage has sat — and the second one was not on the card at all, so a stage holding one order
   * for three weeks looked healthier than a stage holding six from this morning.
   *
   * The stages are drawn as one bar because they are shares of a single queue (`orderQueue` — the
   * page's one definition of "orders to fulfil", so the shares add up to the tile), and each stage
   * states its own count and its oldest wait.
   */
  const pipelineData = useMemo(() => {
    const byStage = new Map<string, number[]>();
    for (const o of orderQueue as any[]) {
      const arr = byStage.get(o.status) ?? [];
      arr.push(ageDays(o.orderDate));
      byStage.set(o.status, arr);
    }
    return PIPELINE
      .map((p) => {
        const ages = byStage.get(p.status) ?? [];
        return {
          label: p.label, note: p.note, status: p.status, count: ages.length,
          oldest: ages.length ? Math.max(...ages) : 0,
          color: STATUS_COLOR[p.status] ?? brand.primary,
        };
      })
      .filter((p) => p.count > 0);
  }, [orderQueue]);
  const pipelineTotal = useMemo(() => pipelineData.reduce((a, b) => a + b.count, 0), [pipelineData]);
  /* Orders the warehouse cannot act on because the stock is still coming from another site. They are
     NOT part of the queue above (the tile does not count them either), so they are named rather than
     folded in — a stage that silently swelled the total would break the one figure this page keeps. */
  const awaitingTransfer = useMemo(
    () => (ordersRes?.data ?? []).filter((o: any) => o.status === BLOCKED_STAGE.status).length,
    [ordersRes],
  );

  /*
   * THE LIVE QUEUE vs FINISHED — two different questions, so not one ring.
   *
   * The donut used to plot readyToDispatch + inTransit + delivered + cancelled as slices of one
   * "64 shipments". They do not belong to one whole: the first two are shipments sitting somewhere
   * RIGHT NOW, the last two are every shipment that has ever finished (the summary SQL counts them
   * with no date bound). So 41% of the ring was cancelled-forever, the centre total measured
   * nothing anybody has to act on, and a warehouse with a clear desk still drew a mostly-grey
   * circle. The chart is the live queue only; the lifetime outcomes are stated underneath as what
   * they are.
   *
   * Each stage carries the status it filters by, so the drill-through is a FIELD rather than a
   * match on the visible label — the words above a warehouse queue get reworded, and a link that
   * breaks when they do is a trap.
   */
  const dispatchMix = useMemo(() => ([
    { name: 'Ready to dispatch', note: 'packed, still here', status: 'PACKED', value: Number(dispSummary.readyToDispatch ?? 0), color: PALETTE.harvest[400] },
    { name: 'On the road', note: 'left, not delivered', status: 'DISPATCHED', value: Number(dispSummary.inTransit ?? 0), color: PALETTE.sky[500] },
  ].filter((d) => d.value > 0)), [dispSummary]);
  const dispatchTotal = useMemo(() => dispatchMix.reduce((a, b) => a + b.value, 0), [dispatchMix]);
  /* The shares are a PARTITION of that queue, so the last stage takes the rounding remainder: two
     halves of 62.5% must print 63% and 37%, not 63% twice and a total of 126. */
  const dispatchRows = useMemo(() => {
    let used = 0;
    return dispatchMix.map((d, i) => {
      const pct = i === dispatchMix.length - 1
        ? Math.max(0, 100 - used)
        : Math.round((d.value / Math.max(1, dispatchTotal)) * 100);
      used += pct;
      return { ...d, pct };
    });
  }, [dispatchMix, dispatchTotal]);
  const shipDelivered = Number(dispSummary.delivered ?? 0);
  const shipCancelled = Number(dispSummary.cancelled ?? 0);
  const shipFinished = shipDelivered + shipCancelled;
  const cancelPct = shipFinished ? Math.round((shipCancelled / shipFinished) * 100) : 0;

  /*
   * WHICH SEVEN — and what happens to the rest.
   *
   * A warehouse is rarely one product short; it is eleven products short, four of them on the same
   * site. This chart drew whatever seven the endpoint happened to return first and said nothing
   * about the others, so a card reading "6 products" sat under a tile reading "12 low-stock items"
   * and the reader had no way to tell which figure was lying.
   *
   * Two fixes, both about honesty rather than space. The rows are ranked by how FAR under the
   * minimum they are (`shortOf`, the same expression the alerts register sorts by), so the seven
   * drawn are the seven worst rather than an arbitrary seven. And whatever does not fit is NAMED
   * under the chart instead of quietly dropped — no scroller inside the card, which would only hide
   * the same rows behind a gesture.
   *
   * The per-warehouse strip answers what the bars cannot: seven product rows spread over three
   * sites hide that ONE site is carrying most of the shortage. It is a partition of the same total
   * — the counts add up to every open breach — and each chip opens its own site's list.
   */
  const LOW_ROWS = 12;
  const lowRanked = useMemo(() => [...lows].sort((a: any, b: any) => shortOf(b) - shortOf(a)), [lows]);
  const lowGap = useMemo(() => lowRanked.slice(0, LOW_ROWS).map((r: any, i: number) => ({
    id: String(r.id ?? `${r.productId}-${r.warehouseId}-${i}`),
    product: String(r.productName ?? '—'),
    code: String(r.warehouseCode ?? '—'),
    available: Number(r.available ?? 0),
    min: Number(r.minLevel ?? 0),
    short: shortOf(r),
    days: ageDays(r.openedAt),
  })), [lowRanked]);
  /* Every track is drawn against the LARGEST minimum on the card, so a 100-unit requirement is twice
     the bar of a 50-unit one and the empty tail of each track is the shortfall to scale — comparable
     across rows instead of six bars that all end at the same place. */
  const lowMaxMin = useMemo(() => lowGap.reduce((m, r) => Math.max(m, r.min), 0), [lowGap]);
  const lowHidden = Math.max(0, lows.length - lowGap.length);
  const lowByWh = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of lows as any[]) {
      const code = String(r.warehouseCode ?? '—');
      m.set(code, (m.get(code) ?? 0) + 1);
    }
    return [...m.entries()].map(([code, count]) => ({ code, count })).sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
  }, [lows]);

  const Kpi = ({ title, value, color, sub, icon, accent = brand.primary, onClick, loading }: {
    title: ReactNode; value: any; color?: string; sub?: ReactNode; icon?: ReactNode; accent?: string; onClick?: () => void; loading?: boolean;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className="group flex h-full min-h-[92px] min-w-[180px] flex-1 items-center gap-3 rounded-card border border-line bg-surface px-4 py-4 text-left transition duration-150 hover:-translate-y-0.5 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-lg transition-transform duration-150 group-hover:scale-105 [&>svg]:size-5" style={{ background: `${accent}1A`, color: accent }}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 truncate text-xs text-muted">{title}</div>
        <div className={cn('type-metric leading-tight', !color && 'text-content')} style={color ? { color } : undefined}>
          {loading ? <Skeleton className="h-6 w-16" /> : typeof value === 'number' ? <CountUp value={value} format={(n) => String(Math.round(n))} /> : value}
        </div>
        {sub && <div className="mt-0.5 text-2xs text-subtle">{sub}</div>}
      </div>
    </button>
  );

  return (
    <div className="space-y-4">
      {/*
        * THE LETTERHEAD — quiet paper, not a marketing banner.
        *
        * This was a slab of saturated green carrying the greeting, one sentence of state and ten
        * translucent chips stacked flat on top of one another. Solid colour edge to edge is the
        * shape of a promo header, and it flattened three DIFFERENT things — who is reading, what
        * the day looks like, which sites are in scope — into one undifferentiated block, with the
        * whole right half of a wide screen left empty.
        *
        * It is a letterhead now, the same construction the company-profile masthead uses: one brand
        * rule along the top edge, a wash that fades the brand tint into the surface, and the content
        * set as separate registers — identity beside its mark, the day as a sentence, the sites
        * below their own hairline under a named label.
        *
        * The mark is the reader's own photo/initials on a hue derived from their name, so it VARIES
        * per person. The lone warehouse glyph that used to sit beside the label went the other way —
        * one fixed icon on the one page it could never distinguish — which is why it was removed.
        */}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-e1">
        <div className="h-1 bg-linear-to-r from-brand-500 via-brand-600 to-brand-700" />
        <div className="bg-linear-to-br from-brand-50 via-brand-50/30 to-surface p-5 dark:from-brand-900/25 dark:via-surface dark:to-surface">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
            <div className="flex min-w-0 items-start gap-3.5">
              {/* The mark sits on a surface plate, not straight on the wash: EntityAvatar tints itself
                  to ~20% of its hue, which reads on plain paper but dissolves against the brand tint
                  behind it — the plate gives every hue the same ground to sit on. */}
              <span className="mt-0.5 grid shrink-0 place-items-center rounded-pill bg-surface p-1 shadow-e1">
                <EntityAvatar name={user?.fullName ?? 'Warehouse'} src={user?.profilePhotoUrl} size={48} />
              </span>
              <div className="min-w-0">
                <div className="type-overline text-brand-600 dark:text-brand-400">Warehouse operations</div>
                {/* Name and role on one line (see `showRole` for when the pill is suppressed). The wave
                    that used to follow the name is gone: at `type-hero` on paper it was the one loud,
                    casual mark on a console whose next row is six operational counts. */}
                <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  <h1 className="type-hero text-content">Hi{user?.fullName ? `, ${user.fullName}` : ''}</h1>
                  {showRole && <RoleBadge role={user!.role} />}
                </div>
              </div>
            </div>

            {/* The date, right-aligned the way a letterhead carries it. It is also the only thing on
                the page that says WHEN "today", "oldest waiting 8d" and the 14-day trend are counted
                from — and it is the one fact here that does not repeat a tile underneath. */}
            <div className="shrink-0 rounded-card border border-line bg-surface px-4 py-2.5 shadow-e1 lg:min-w-[168px] lg:text-right">
              <div className="type-overline text-subtle">Today</div>
              <div className="mt-1 type-body-strong text-content">{formatIst(new Date(), 'ddd, DD MMM YYYY')}</div>
            </div>
          </div>

          {/* Sites, below their own hairline and under a label. A bare strip of chips never said what
              it was a list OF; on green it also had to be read through a translucent wash. */}
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-line pt-3.5">
            <span className="shrink-0 type-overline text-subtle">Sites in scope</span>
            {whLoading ? <Skeleton className="h-6 w-48" /> : whs.length ? (
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                {/*
                  * Each site is a BUTTON, not a decorative chip.
                  *
                  * The strip named the warehouses in scope and then did nothing when you pressed one,
                  * which is the worst of both: it looks like a filter and behaves like a label. Every
                  * chip now opens that warehouse's dispatch register — the same destination the
                  * "Where the work sits" rows use, so one warehouse means one place wherever you
                  * click it, and `?wh=` is a filter that page genuinely reads (`useUrlString('wh')`).
                  *
                  * Dispatches rather than the Warehouses master on purpose: a Warehouse Manager holds
                  * no WAREHOUSE.VIEW, so `/inventory/warehouses` would 403 them straight back to
                  * /home. This target works for every role that can open this page.
                  */}
                {(allSites ? whs : whs.slice(0, 6)).map((w: any) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => navigate(`/warehouse/dispatches?wh=${w.id}`)}
                    title={`Open dispatches for ${w.code} · ${w.name}`}
                    aria-label={`Open dispatches for ${w.code}, ${w.name}`}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-pill border border-line bg-surface px-2.5 py-1 shadow-e1 transition-colors hover:border-brand-400 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:hover:bg-brand-900/25"
                  >
                    <span className="type-label-strong text-content">{w.code}</span>
                    <span className="truncate type-label text-muted">{w.name}</span>
                  </button>
                ))}
                {whs.length > 6 && (
                  <button
                    type="button"
                    onClick={() => setAllSites((v) => !v)}
                    aria-expanded={allSites}
                    className="inline-flex items-center rounded-pill border border-brand-500/30 bg-brand-50 px-2.5 py-1 type-label-strong text-brand-700 transition-colors hover:border-brand-500 hover:bg-brand-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50"
                  >
                    {allSites ? 'Show fewer' : `+${whs.length - 6} more`}
                  </button>
                )}
              </div>
            ) : (
              <span className="type-body text-muted">No warehouse assigned yet — ask an admin to assign you one.</span>
            )}
          </div>
        </div>
      </div>

      {/*
        * KPI band on the app's shared StatCard, replacing this page's own one-off tile.
        *
        * Every tile now carries its metric's OWN tint (the same accent ladder the registers use), and
        * every hint states a fact rather than repeating the label: how old the oldest waiting order
        * is, how many went out today, how long the worst stock breach has been open. A count with no
        * hint reads identically on a good day and a bad one.
        */}
      <Stagger className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          tone="brand" icon={<Package size={18} />} label="Orders to fulfil"
          value={ordLoading ? <Skeleton className="h-6 w-12" /> : <CountUp value={orderQueue.length} format={(n) => String(Math.round(n))} />}
          hint={ordersCapped
            ? `in the latest ${ordersRes?.data?.length ?? 0} orders${queueAge.stale ? ` · ${queueAge.stale} over a week` : ''}`
            : queueAge.stale ? `${queueAge.stale} waiting over a week` : orderQueue.length ? 'approved · in fulfillment · packed' : 'desk is clear'}
          onClick={() => navigate('/orders')}
        />
        <StatCard
          tone="sky" icon={<Truck size={18} />} label="Ready to dispatch"
          value={<CountUp value={Number(dispSummary.readyToDispatch ?? 0)} format={(n) => String(Math.round(n))} />}
          hint={`${Number(dispSummary.inTransit ?? 0)} in transit · ${Number(dispSummary.dispatchedToday ?? 0)} went out today`}
          onClick={() => navigate('/warehouse/dispatches?status=PACKED')}
        />
        <StatCard
          tone="gold" icon={<ArrowDownToLine size={18} />} label="Incoming transfers"
          value={<CountUp value={badges.transfersAwaitingReceipt ?? incoming.length} format={(n) => String(Math.round(n))} />}
          hint={incoming.length ? 'stock to receive' : 'nothing on its way in'}
          onClick={() => navigate('/inventory/master-stock')}
        />
        <StatCard
          tone="success" icon={<ArrowUpFromLine size={18} />} label="Outgoing transfers"
          value={<CountUp value={badges.transfersAwaitingDispatch ?? 0} format={(n) => String(Math.round(n))} />}
          hint={(badges.transfersAwaitingDispatch ?? 0) ? 'stock to send' : 'nothing waiting to go'}
          onClick={() => navigate('/inventory/master-stock')}
        />
        {/* The third kind of transfer work, at the same size as the two above it. A transfer received
            short is the only one of the three where stock has gone missing rather than merely sitting
            somewhere — it was the one this dashboard did not mention at all. */}
        <StatCard
          tone={(badges.transfersAwaitingResolution ?? 0) ? 'danger' : 'neutral'}
          icon={<PackageX size={18} />} label="Short deliveries"
          value={<CountUp value={badges.transfersAwaitingResolution ?? 0} format={(n) => String(Math.round(n))} />}
          hint={(badges.transfersAwaitingResolution ?? 0)
            ? 'arrived short or damaged · nobody has settled it'
            : 'every transfer arrived complete'}
          onClick={() => navigate('/inventory/master-stock?tab=transfers&status=PARTIAL')}
        />
        <StatCard
          tone={lows.length ? 'warning' : 'neutral'} icon={<TriangleAlert size={18} />} label="Low-stock items"
          value={lowLoading ? <Skeleton className="h-6 w-12" /> : <CountUp value={lows.length} format={(n) => String(Math.round(n))} />}
          hint={lows.length ? (lowAge ? `below minimum · oldest ${lowAge}d open` : 'below minimum') : 'every item is above its minimum'}
          onClick={() => navigate('/inventory/low-stock')}
        />
        <StatCard
          tone={nsUnits ? 'danger' : 'neutral'} icon={<Ban size={18} />} label="Non-sellable units"
          value={<CountUp value={nsUnits} format={(n) => String(Math.round(n))} />}
          hint={nsUnits
            ? <>{nsValue != null && <><b className="text-content">{inr(nsValue)}</b> of stock{nsWhy ? ' · ' : ''}</>}{nsWhy}</>
            : 'nothing blocked'}
          onClick={() => navigate('/inventory/stock?tab=non-sellable')}
        />
      </Stagger>

      {/* Charts — order flow */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" title={<span className="flex items-center gap-2"><TrendingUp className="size-4 text-brand-600" /> Orders received</span>} extra="Last 14 days · click a day">
          {trendTotal ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trendData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}
                onClick={(s: any) => s?.activeLabel && navigate(`/orders?from=${s.activeLabel}&to=${s.activeLabel}`)}>
                <defs>
                  <linearGradient id="whmTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={brand.primary} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={brand.primary} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tickFormatter={(v: string) => String(v).slice(5)} tickLine={false} axisLine={false} minTickGap={20} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={28} tick={{ fontSize: 11 }} />
                <RTooltip content={<ChartTooltip unit="orders" />} cursor={{ stroke: 'var(--gb-border-strong)' }} />
                <Area type="monotone" dataKey="count" name="Orders" stroke={brand.primary} strokeWidth={2} fill="url(#whmTrendFill)" dot={false} activeDot={{ r: 4 }} cursor="pointer" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart text="No orders received in the last 14 days." />}
        </Card>

        {/*
          * "IN FLIGHT" is aviation, and nothing here flies.
          *
          * The title, the centre caption and the empty state all borrowed a word a storeman does
          * not use for a van on the Pune road, so the card's whole job — what is packed, what has
          * left, what has not landed — was said in a metaphor. It now says it in the words the
          * warehouse says it in.
          *
          * The ring went with it. Two slices is not a part-to-whole picture; it is a ratio, and it
          * spent 200px of circle plus a centre number to state what one bar and two labelled lines
          * state outright. The bar runs in the order the work moves — waiting on the left, on the
          * road on the right — so its width reads as "how much of this is still sitting in my
          * warehouse", and each stage opens its own slice of the dispatch register.
          */}
        {/* `self-start`: this card is a short list, and stretching it to the height of the 14-day
            chart beside it only draws a half-empty rectangle. Ending where the content ends reads as
            deliberate; padding it out to fill the row is what filler looks like. */}
        <Card className="self-start" title={<span className="flex items-center gap-2"><Truck className="size-4 text-sky-600" /> Shipments in progress</span>} extra="right now · click a stage">
          {dispatchTotal ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="type-metric text-content"><CountUp value={dispatchTotal} format={(n) => String(Math.round(n))} /></span>
                <span className="type-caption text-muted">shipments not delivered yet</span>
              </div>
              {/* 2px of surface between the segments (`gap-0.5`) so two fills never touch — the same
                  separation the stacked bars elsewhere use. */}
              <div className="mt-3 flex h-4 w-full gap-0.5">
                {dispatchRows.map((d) => (
                  <button key={d.status} type="button" aria-label={`${d.name}: ${d.value} shipments`}
                    onClick={() => navigate(`/warehouse/dispatches?status=${d.status}`)}
                    style={{ flexGrow: d.value, background: d.color }}
                    className="h-full min-w-[10px] rounded-pill transition hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40" />
                ))}
              </div>
              {/* Identity is never colour alone: every stage names itself, and says in plain words
                  what that stage MEANS before it gives the count. */}
              <div className="mt-2.5">
                {dispatchRows.map((d) => (
                  <button key={d.status} type="button" onClick={() => navigate(`/warehouse/dispatches?status=${d.status}`)}
                    className="flex w-full items-center gap-2 rounded-md px-1 py-2 text-left transition hover:bg-surface-2">
                    <span className="inline-block size-2.5 shrink-0 rounded-full" style={{ background: d.color }} />
                    <span className="shrink-0 type-body text-content">{d.name}</span>
                    <span className="min-w-0 truncate type-caption text-subtle">{d.note}</span>
                    <span className="ml-auto type-body-strong tabular-nums text-content">{d.value}</span>
                    <span className="w-9 shrink-0 text-right type-caption tabular-nums text-subtle">{d.pct}%</span>
                  </button>
                ))}
              </div>
            </>
          ) : <EmptyChart text="Nothing packed waiting to go out, and nothing on the road." />}

          {/*
            * Finished shipments live BELOW the ring, not in it. They are lifetime totals with no date
            * bound, so slicing them against two live queues made a whole that measures nothing. The
            * cancellation share is the one number here worth a colour: it is a rate, not a queue.
            */}
          {shipFinished > 0 && (
            <div className="mt-3 border-t border-line pt-2.5">
              <div className="mb-1 type-overline text-subtle">Already finished · all time</div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 type-caption text-muted">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full" style={{ background: PALETTE.brand[500] }} />
                  Delivered <b className="tabular-nums text-content">{shipDelivered}</b>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full" style={{ background: neutralColor(isDark) }} />
                  Cancelled <b className="tabular-nums text-content">{shipCancelled}</b>
                  <span className={cn('tabular-nums', cancelPct >= 25 ? 'text-danger-600 dark:text-danger-500' : 'text-subtle')}>
                    ({cancelPct}%)
                  </span>
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Charts — pipeline & stock */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card
          className="self-start"
          title={<span className="flex items-center gap-2"><ClipboardList className="size-4 text-brand-600" /> Fulfilment pipeline</span>}
          extra={<span className="type-caption text-subtle">open orders by stage · click a stage</span>}
        >
          {pipelineData.length ? (
            <>
              <div className="flex h-4 w-full gap-0.5">
                {pipelineData.map((p) => (
                  <button key={p.status} type="button" aria-label={`${p.label}: ${p.count} orders`}
                    onClick={() => navigate(`/orders?status=${p.status}`)}
                    style={{ flexGrow: p.count, background: p.color }}
                    className="h-full min-w-[10px] rounded-pill transition hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40" />
                ))}
              </div>

              <div className="mt-2 grid grid-cols-[minmax(0,1fr)_54px_72px] items-end gap-x-3 border-b border-line pb-1 type-overline text-subtle">
                <span>Stage</span>
                <span className="text-right">Orders</span>
                <span className="text-right">Oldest wait</span>
              </div>
              <div className="divide-y divide-line">
                {pipelineData.map((p) => (
                  <button key={p.status} type="button" onClick={() => navigate(`/orders?status=${p.status}`)}
                    className="grid w-full grid-cols-[minmax(0,1fr)_54px_72px] items-center gap-x-3 py-2 text-left transition hover:bg-surface-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="inline-block size-2.5 shrink-0 rounded-full" style={{ background: p.color }} />
                      <span className="min-w-0">
                        <span className="block truncate type-body text-content">{p.label}</span>
                        <span className="block truncate type-caption text-subtle">{p.note}</span>
                      </span>
                    </span>
                    <span className="text-right type-body-strong tabular-nums text-content">{p.count}</span>
                    {/* Amber only past a week — the age is the half of this card that says whether a
                        stage is merely busy or actually stuck. */}
                    <span className="text-right tabular-nums">
                      {p.oldest >= 7
                        ? <span className="type-body-medium text-warning-600 dark:text-harvest-300">{p.oldest} days</span>
                        : <span className="type-body text-muted">{p.oldest} {p.oldest === 1 ? 'day' : 'days'}</span>}
                    </span>
                  </button>
                ))}
              </div>

              {/* Orders nobody here can pick, named rather than mixed into the queue above. */}
              {awaitingTransfer > 0 && (
                <button type="button" onClick={() => navigate(`/orders?status=${BLOCKED_STAGE.status}`)}
                  className="mt-2 block w-full rounded-card border border-warning-500/30 bg-warning-500/[0.08] px-3 py-2 text-left type-caption text-warning-600 transition hover:bg-warning-500/[0.14] dark:text-harvest-300">
                  <b>{awaitingTransfer}</b> more {awaitingTransfer === 1 ? 'order is' : 'orders are'} waiting on stock from another warehouse — not pickable here, so not in the {pipelineTotal} above.
                </button>
              )}
            </>
          ) : <EmptyChart text="Nothing waiting to be picked, packed or loaded." />}
        </Card>

        {/*
          * A METER PER PRODUCT, not two bars that say the same thing twice.
          *
          * This was a grouped bar chart: an amber "available" bar and a grey "min level" bar per row.
          * Every warehouse sets its minimums in round numbers, so the grey bars all ended at 50 and
          * drew a straight grey wall down the card — six rows of chart to say what one column of
          * numbers says better. Worse, the one figure the reader is actually after, HOW SHORT each
          * item is, was nowhere on the card: it had to be eyeballed as the distance between two bars.
          *
          * "Available against its minimum" is a ratio against a limit, and the form for that is a
          * meter: the track IS the minimum, the fill is what is on hand, and the empty tail is the
          * shortfall — drawn to scale against the biggest requirement on the card, so the tails are
          * comparable between rows. The numbers sit beside it in full, ranked deepest-shortfall
          * first, with the age of the breach — a product 91 short since July is a different problem
          * from one that dipped under this morning.
          *
          * And it is a LIST, not a chart with a decorative cap: every open breach is drawn, up to a
          * readable dozen, with the rest named in amber underneath rather than trimmed to make the
          * card a tidy rectangle.
          */}
        <Card
          title={<span className="flex items-center gap-2"><TriangleAlert className="size-4 text-warning-600" /> Low stock — what to reorder</span>}
          extra={<span className="type-caption text-subtle">deepest shortfall first · click a row for its warehouse</span>}
        >
          {lowLoading ? <div className="py-6"><Skeleton className="h-40 w-full" /></div> : lowGap.length ? (
            <>
              {/* The meter column is DROPPED below `sm` rather than squeezed — at 390px it collapsed
                  to nothing while its heading ran through the one beside it, and a bar a few pixels
                  wide measures nothing anyway. The figures it annotates ride under the product name
                  instead, which is the same trade the orders and transfer panels make. */}
              <div className="grid grid-cols-[minmax(0,1fr)_62px_52px] sm:grid-cols-[minmax(0,1.15fr)_minmax(64px,1.5fr)_82px_62px] items-end gap-x-3 border-b border-line pb-1.5 type-overline text-subtle">
                <span className="whitespace-nowrap">Product</span>
                {/* Wraps: nowrap ran it 22px into "Short by" in the half-width card at 1024px. */}
                <span className="hidden sm:block">On hand against its minimum</span>
                <span className="whitespace-nowrap text-right">Short by</span>
                <span className="whitespace-nowrap text-right">Open</span>
              </div>
              <div className="divide-y divide-line">
                {lowGap.map((r) => {
                  const hue = whColors[r.code] ?? entityHue(r.code);
                  const cover = r.min > 0 ? Math.min(100, (r.available / r.min) * 100) : 0;
                  const track = lowMaxMin > 0 ? Math.max(12, (r.min / lowMaxMin) * 100) : 100;
                  return (
                    <button key={r.id} type="button"
                      onClick={() => navigate(`/inventory/low-stock?wh=${encodeURIComponent(r.code)}`)}
                      className="grid w-full grid-cols-[minmax(0,1fr)_62px_52px] sm:grid-cols-[minmax(0,1.15fr)_minmax(64px,1.5fr)_82px_62px] items-center gap-x-3 py-2 text-left transition hover:bg-surface-2">
                      <span className="min-w-0">
                        <span className="block truncate type-body-medium text-content">{r.product}</span>
                        {/* Wraps: at 320px the site chip left "23 of 49" 14px, so the figures drop under it. */}
                        <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          {/* The site wears the same chip it wears in the strip below, so a row and its
                              count are found by the same colour. A grey caption plus a coloured dot said
                              the same thing twice. It never truncates — a "WH-P…" names nothing; the
                              product above it is the label with room to give. */}
                          <span className="shrink-0 rounded-pill px-1.5 py-px type-caption"
                            style={{
                              background: `color-mix(in srgb, ${hue} 12%, transparent)`,
                              color: hue,
                              boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 30%, transparent)`,
                            }}>{r.code}</span>
                          <span className="truncate type-caption tabular-nums text-muted sm:hidden">
                            <b className="text-content">{fmtQty(r.available)}</b> of {fmtQty(r.min)}
                          </span>
                        </span>
                      </span>

                      {/*
                        * Track = this product's minimum, fill = what is actually on the shelf.
                        *
                        * The track and the figure beside it get a GRID rather than a flex row: as a
                        * flex child the track was shrunk by the width of its own label, so a 100-unit
                        * requirement drew only 1.6× the bar of a 50-unit one instead of 2×, and the
                        * one thing this form promises — tails that are comparable between rows —
                        * quietly did not hold.
                        */}
                      <span className="hidden min-w-0 grid-cols-[minmax(0,1fr)_74px] items-center gap-2 sm:grid">
                        <span className="h-2.5 min-w-0 overflow-hidden rounded-pill bg-surface-3" style={{ width: `${track}%` }}>
                          <span className="block h-full rounded-pill" style={{ width: `${Math.max(2, cover)}%`, background: PALETTE.warning[600] }} />
                        </span>
                        <span className="truncate text-right type-caption tabular-nums text-muted">
                          <b className="text-content">{fmtQty(r.available)}</b> of {fmtQty(r.min)}
                        </span>
                      </span>

                      <span className="text-right type-body-strong tabular-nums text-danger-600 dark:text-danger-500">{fmtQty(r.short)}</span>

                      {/* Amber only once a breach has sat a week — a colour that is always on says nothing. */}
                      <span className="text-right tabular-nums">
                        {r.days >= 7
                          ? <span className="type-body-medium text-warning-600 dark:text-harvest-300">{r.days}d</span>
                          : <span className="type-body text-muted">{r.days}d</span>}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* WHERE the shortage sits. Each site wears the hue it wears in the table above, and
                  the counts are a partition of every open breach — so they add up, and each one
                  opens that site's list rather than the whole register. */}
              {lowByWh.length > 1 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-line pt-2.5">
                  <span className="mr-0.5 type-overline text-subtle">Below minimum by warehouse</span>
                  {lowByWh.map((w) => {
                    const hue = whColors[w.code] ?? entityHue(w.code);
                    return (
                      <button key={w.code} type="button"
                        onClick={() => navigate(`/inventory/low-stock?wh=${encodeURIComponent(w.code)}`)}
                        className="inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 type-caption transition hover:opacity-80"
                        style={{
                          background: `color-mix(in srgb, ${hue} 12%, transparent)`,
                          color: hue,
                          boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 32%, transparent)`,
                        }}>
                        {w.code} <b className="tabular-nums">{w.count}</b>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* The rows that did not fit are NAMED, not dropped — a card that shows six of twelve
                  without saying so is why the tile and this card could disagree. */}
              {lowHidden > 0 && (
                <button type="button" onClick={() => navigate('/inventory/low-stock')}
                  className="mt-2 block w-full rounded-card border border-warning-500/30 bg-warning-500/[0.08] px-3 py-2 text-left type-caption text-warning-600 transition hover:bg-warning-500/[0.14] dark:text-harvest-300">
                  <b>{lowHidden}</b> more {lowHidden === 1 ? 'product is' : 'products are'} below minimum, past the {lowGap.length} deepest shown here — open the full list →
                </button>
              )}
            </>
          ) : <EmptyChart text="No items below minimum. 🎉" />}
        </Card>
      </div>

      {/*
        * WHERE the work sits — only when there is more than one warehouse to tell apart.
        *
        * Everything above this is a company-wide total, which is the whole answer for a Warehouse
        * Manager and half an answer for an Admin looking at ten sites: "97 to fulfil" never said
        * which warehouse is behind. Each row is one site, ranked by what is queued on it, with its
        * own identity hue and its oldest waiting order named in amber once it passes a week.
        */}
      {byWarehouse.length > 1 && (
        <Card
          title={<span className="flex items-center gap-2"><Layers className="size-4" style={{ color: PALETTE.plum[500] }} /> Where the work sits</span>}
          extra={<span className="type-caption text-subtle">{byWarehouse.length} of {whs.length} warehouses have work · click a row to open its dispatches</span>}
        >
          {/*
            * A real TABLE, not a row of optional chips.
            *
            * The first cut hung each figure inline and only rendered it when non-zero, so no two rows
            * put the same number in the same place and the right-hand edge came out ragged. Worse, a
            * bare "8d" or "2 low" names nothing: the reader had to guess that one was an age and the
            * other a count of breaches. Fixed columns under real headings fix both — every figure
            * sits under the word that says what it is, and a zero prints an em dash rather than
            * vanishing and shunting its neighbours sideways.
            *
            * `grid-cols-[...]` is shared by the header and every row, which is what actually holds
            * the columns in line.
            */}
          <div className="min-w-0 overflow-x-auto">
            <div className="min-w-[680px]">
              <div className="grid grid-cols-[minmax(140px,1.4fr)_minmax(120px,2fr)_96px_88px_88px] items-end gap-x-4 border-b border-line px-1 pb-2 type-overline text-subtle">
                <span>Warehouse</span>
                <span>Orders to fulfil</span>
                <span className="text-right">Oldest wait</span>
                <span className="text-right">Low stock</span>
                <span className="text-right">Incoming</span>
              </div>

              <div className="divide-y divide-line">
                {byWarehouse.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate(`/warehouse/dispatches?wh=${r.id}`)}
                    className="grid w-full grid-cols-[minmax(140px,1.4fr)_minmax(120px,2fr)_96px_88px_88px] items-center gap-x-4 px-1 py-2.5 text-left transition hover:bg-surface-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {/* The site's own hue, so a row is findable by colour on a ten-warehouse list. */}
                      <span className="inline-block size-2.5 shrink-0 rounded-full" style={{ background: whColors[r.code] }} />
                      <span className="min-w-0">
                        <span className="block truncate type-body-medium text-content">{r.code}</span>
                        <span className="block truncate type-caption text-subtle">{r.name}</span>
                      </span>
                    </span>

                    {/* Count and bar in ONE column, so the number is always at the same x. */}
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="w-7 shrink-0 text-right type-body-strong tabular-nums text-content">{r.toFulfil}</span>
                      <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-pill bg-surface-3">
                        <span className="block h-full rounded-pill" style={{ width: `${whMax ? Math.max(4, (r.toFulfil / whMax) * 100) : 0}%`, background: whColors[r.code] }} />
                      </span>
                    </span>

                    {/* Amber only once it crosses a week — a colour that is always on says nothing. */}
                    <span className="text-right tabular-nums">
                      {r.oldest >= 7
                        ? <span className="type-body-medium text-warning-600 dark:text-harvest-300">{r.oldest} days</span>
                        : r.oldest
                          ? <span className="type-body text-muted">{r.oldest} days</span>
                          : <span className="type-body text-subtle">—</span>}
                    </span>

                    <span className="text-right tabular-nums">
                      {r.lowStock
                        ? <span className="type-body-medium text-danger-600 dark:text-danger-500">{r.lowStock}</span>
                        : <span className="type-body text-subtle">—</span>}
                    </span>

                    <span className="text-right tabular-nums">
                      {r.incoming
                        ? <span className="type-body-medium text-sky-600 dark:text-sky-400">{r.incoming}</span>
                        : <span className="type-body text-subtle">—</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Name the gap rather than let the rows quietly fail to add up to the headline above. */}
          {unassigned > 0 && (
            <div className="mt-3 rounded-card border border-warning-500/30 bg-warning-500/[0.08] px-3 py-2 type-caption text-warning-600 dark:text-harvest-300">
              <b>{unassigned}</b> order{unassigned === 1 ? ' is' : 's are'} not assigned to a warehouse yet, so {unassigned === 1 ? 'it appears' : 'they appear'} in no row above.
            </div>
          )}
        </Card>
      )}

      {/* Actionable queues */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Orders to fulfil */}
        <Card
          title={<span className="flex items-center gap-2"><ClipboardList className="size-4 text-brand-600" /> Orders to fulfil</span>}
          extra={orderQueue.length > 7
            ? <span className="type-caption text-subtle">oldest 7 of {orderQueue.length}</span>
            : undefined}
        >
          {ordLoading ? <ListSkeleton /> : orderQueue.length ? (
            <div className="divide-y divide-line">
              {/*
                * Four columns, not three. What is ON the order was a subtitle under its number, which
                * left the row with one narrow pair of lines on the left and every value pinned to the
                * right edge — a 500px dead stripe down the middle of a 730px card. Three short labels
                * cannot fill a row that wide, so the gaps had nowhere to go but into one chasm.
                *
                * The contents get their own column, the way the transfer panel beside this one already
                * does. The space is now carrying the answer to "what is it", the two panels read the
                * same way, and every gutter lands within ~50px of its neighbours.
                */}
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(64px,0.5fr)_minmax(96px,0.9fr)] sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(64px,0.5fr)_minmax(96px,0.9fr)] items-end gap-x-4 pb-1.5 type-overline text-subtle">
                <span className="whitespace-nowrap">Order</span>
                <span className="hidden whitespace-nowrap sm:block">Contents</span>
                <span className="whitespace-nowrap text-right">Waiting</span>
                <span className="whitespace-nowrap text-right">Stage</span>
              </div>
              {orderQueue.slice(0, 7).map((o: any) => (
                <button key={o.id} type="button" onClick={() => navigate(`/orders/${o.id}`)}
                  className="grid w-full grid-cols-[minmax(0,1fr)_minmax(64px,0.5fr)_minmax(96px,0.9fr)] sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(64px,0.5fr)_minmax(96px,0.9fr)] items-center gap-x-4 py-2 text-left transition hover:bg-surface-2">
                  {(() => {
                    /* One product NAMED and the rest counted, the same shape the transfer panel
                       beside this one uses — a row of five comma-joined SKUs is unreadable at this
                       width, and every row of it looked identical. The pack size comes along
                       (`variantText`) because seven orders of "GreenGro Bio" are told apart by their
                       500 ml / 1 L, not by the name. */
                    const items: any[] = o.products ?? [];
                    const first = items[0];
                    const label = first
                      ? [first.name, Number(first.packSize) > 0 ? variantText(first.packSize, first.uom, first.variantLabel) : (first.variantLabel || '')]
                        .filter(Boolean).join(' ')
                      : '';
                    const units = items.reduce((a, p) => a + Number(p.qty ?? 0), 0);
                    return (
                      <>
                        <div className="min-w-0">
                          {/* Breaks instead of truncating: at 320px "GB/2026/07/00037" still showed 47%. */}
                          <div className="type-body-medium text-content [overflow-wrap:anywhere]">{o.orderNo}</div>
                          {/* Below `sm` the contents column is dropped rather than squeezed — at 354px a
                              four-column row shaved the order number down to "GB/2026…", which made every
                              row identical. It goes back under the number, where it started. */}
                          <ContentsCell className="mt-0.5 sm:hidden" label={label} more={Math.max(0, items.length - 1)} qty={units} />
                        </div>
                        <ContentsCell className="hidden sm:flex" label={label} more={Math.max(0, items.length - 1)} qty={units} />
                      </>
                    );
                  })()}
                  {/*
                    * Age gets its own fixed column under the "waiting" heading. Inline it was a bare
                    * "8d" floated against status tags of three different widths — nothing lined up,
                    * and the number named nothing.
                    */}
                  <span className="text-right tabular-nums">
                    {(() => { const a = ageDays(o.orderDate); return a >= 7
                      ? <span className="type-body-medium text-warning-600 dark:text-harvest-300">{a} days</span>
                      : <span className="type-body text-muted">{a} {a === 1 ? 'day' : 'days'}</span>; })()}
                  </span>
                  <span className="flex justify-end">
                    <Tag tone={ORDER_TONE[o.status] ?? 'neutral'}>{orderStatusLabel(o.status)}</Tag>
                  </span>
                </button>
              ))}
            </div>
          ) : <Empty icon={<PackageCheck className="size-6" />} text="Nothing to pack or dispatch right now." />}
        </Card>

        {/*
          * BOTH directions of stock movement, in the card that used to hold one line and half a
          * screen of white space.
          *
          * "Outgoing transfers" had a KPI tile and no list anywhere in the app's warehouse home — a
          * queue of seven with nothing saying what they were. And an incoming row read "1 item(s) ·
          * 9 units", which is not something a storeman can act on: what he needs before walking to
          * the bay is the product, the other warehouse, and how long it has been on the road.
          */}
        <Card
          title={<span className="flex items-center gap-2"><ArrowDownToLine className="size-4 text-sky-600" /> Stock on the move</span>}
          extra={<span className="type-caption text-subtle">{incoming.length} in · {outgoing.length} out</span>}
        >
          <TransferList
            heading="Incoming — to receive"
            tone="sky"
            rows={incoming}
            counterpartLabel="From"
            counterpartOf={(r: any) => r.from}
            emptyText="No incoming transfers on the road."
            onOpen={() => navigate('/inventory/master-stock?tab=transfers')}
          />

          <div className="mt-4 border-t border-line pt-3">
            <TransferList
              heading="Outgoing — to dispatch"
              tone="gold"
              rows={outgoing}
              counterpartLabel="To"
              counterpartOf={(r: any) => r.to}
              emptyText="Nothing waiting to go out."
              onOpen={() => navigate('/inventory/master-stock?tab=transfers')}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * One direction of stock movement.
 *
 * Both lists answer the same four questions in the same four columns — which transfer, what is on
 * it, which warehouse is on the other end, and how long it has sat — so a reader learns the shape
 * once. Age is the column that ranks the list, so the rows arrive sorted by it and it turns amber
 * past a week: stock that has been "on the road" for a fortnight is not in transit, it is lost.
 */
function TransferList({ heading, tone, rows, counterpartLabel, counterpartOf, emptyText, onOpen }: {
  heading: string;
  tone: 'sky' | 'gold';
  rows: any[];
  counterpartLabel: 'From' | 'To';
  counterpartOf: (r: any) => string;
  emptyText: string;
  onOpen: () => void;
}) {
  const dot = tone === 'sky' ? PALETTE.sky[500] : PALETTE.harvest[400];
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 type-label-strong text-content">
          <span className="inline-block size-2 rounded-full" style={{ background: dot }} />
          {heading}
        </span>
        <span className="type-caption tabular-nums text-subtle">{rows.length}</span>
      </div>

      {rows.length ? (
        <div className="divide-y divide-line">
          {/*
            * FOUR columns, not three.
            *
            * What is ON the transfer used to be a subtitle under its number, which left the first
            * column holding two short lines while `1fr` handed it all the leftover width — so every
            * row had a dead stripe down its middle and the real columns crowded the right edge. The
            * contents get their own column: the space is now carrying the answer to "what is it",
            * which is the thing a reader wants next after "which transfer".
            *
            * Both flexible columns are `minmax(…, fr)` so they share the slack instead of one
            * soaking it up, and `gap-x-4` keeps a real gutter between them rather than letting
            * neighbouring values touch.
            */}
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,0.72fr)_68px] sm:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)_minmax(0,0.58fr)_68px] items-end gap-x-4 pb-1 type-overline text-subtle">
            <span className="whitespace-nowrap">Transfer</span>
            <span className="hidden whitespace-nowrap sm:block">Contents</span>
            <span className="whitespace-nowrap">{counterpartLabel}</span>
            <span className="whitespace-nowrap text-right">Waiting</span>
          </div>
          {rows.slice(0, 5).map((r) => {
            const contents = r.product || `${r.items} item${r.items === 1 ? '' : 's'}`;
            return (
              <button key={r.id} type="button" onClick={onOpen}
                className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,0.72fr)_68px] sm:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)_minmax(0,0.58fr)_68px] items-center gap-x-4 py-2 text-left transition hover:bg-surface-2">
                <span className="min-w-0">
                  <span className="block type-body-medium text-content [overflow-wrap:anywhere]">{r.transferNo}</span>
                  {/* A consolidation transfer exists BECAUSE an order is waiting on it — naming that
                      order is why the transfer is urgent, so it sits under its number. */}
                  {r.orderRef && <span className="block truncate type-caption text-subtle">for {r.orderRef}</span>}
                  {/* Below `sm` the contents column is dropped rather than squeezed (see the orders
                      panel) — it rides under the transfer number instead. */}
                  <ContentsCell className="mt-0.5 sm:hidden" label={contents} more={r.more} qty={r.qty} />
                </span>
                <ContentsCell className="hidden sm:flex" label={contents} more={r.more} qty={r.qty} />
                <span className="truncate type-body-medium text-content">{counterpartOf(r)}</span>
                <span className="text-right tabular-nums">
                  {r.days >= 7
                    ? <span className="type-body-medium text-warning-600 dark:text-harvest-300">{r.days}d</span>
                    : <span className="type-body text-muted">{r.days}d</span>}
                </span>
              </button>
            );
          })}
          {rows.length > 5 && (
            <button type="button" onClick={onOpen} className="w-full py-1.5 text-left type-caption text-brand-700 hover:underline dark:text-brand-400">
              {rows.length - 5} more →
            </button>
          )}
        </div>
      ) : (
        <p className="py-3 type-body text-subtle">{emptyText}</p>
      )}
    </div>
  );
}


/**
 * WHAT IS ON IT — the product as an identity chip, the size as a figure.
 *
 * Both queue panels used to print this as one grey sentence with every part strung together by
 * middots: "GreenGro Bio 500 ml · +3 more · 21 units". Three different things — which product, how
 * many others came with it, how much stock — wore the same weight and the same ink, so five rows of
 * the same product read as five identical grey smears and nothing in the column could be found by
 * eye. Dot-joining is how this app writes a SENTENCE; an identifier gets a chip.
 *
 * So the product wears its own stable hue (`entityHue` — the same colour that product carries
 * everywhere else in the app, and a colour that VARIES per row rather than one house tint on all of
 * them), the overflow is plainly a count and not part of the name, and the quantity is a figure in
 * content ink because it is the number a storeman actually reads off the row.
 */
function ContentsCell({ label, more, qty, className }: {
  label?: string | null;
  /** How many OTHER products ride along — rendered as a count, never folded into the name. */
  more?: number;
  qty?: number;
  className?: string;
}) {
  if (!label) return <span className={cn('type-body text-subtle', className)}>—</span>;
  /*
   * The hue keys off the PRODUCT, not the pack. "AGN 0.5L" (the transfer panel, which prints the
   * stored variant label) and "AGN 500 ml" (the orders panel, which derives the pack from packSize)
   * are the same product seen through two panels, and an identity colour that changes between them
   * is worse than no colour at all. The trailing measure is dropped for the HASH only — the label
   * keeps it, because the pack size is what tells two rows of the same product apart.
   */
  const hue = entityHue(label.replace(/\s+\d+(?:\.\d+)?\s*[a-zA-Z]{0,3}$/, '') || label);
  return (
    <span className={cn('flex min-w-0 items-center gap-1.5', className)}>
      <span
        className="min-w-0 truncate rounded-pill px-2 py-0.5 type-caption-strong"
        title={label}
        style={{
          background: `color-mix(in srgb, ${hue} 13%, transparent)`,
          color: hue,
          boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 30%, transparent)`,
        }}
      >
        {label}
      </span>
      {!!more && more > 0 && (
        <span className="shrink-0 rounded-pill bg-surface-3 px-1.5 py-0.5 type-caption text-muted" title={`+${more} more product` + (more === 1 ? ' on this one' : 's on this one')}>+{more}</span>
      )}
      {!!qty && qty > 0 && (
        <span className="shrink-0 type-caption tabular-nums text-subtle"><b className="text-content">{fmtQty(qty)}</b> units</span>
      )}
    </span>
  );
}

/** Themed tooltip shared by every chart — semantic classes so it flips light/dark automatically. */
function ChartTooltip({ active, payload, label, unit }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-surface px-3 py-2 text-xs shadow-pop">
      {label != null && <div className="mb-0.5 font-semibold text-content">{String(label)}</div>}
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-1.5 text-muted">
          <span className="inline-block size-2 rounded-full" style={{ background: p.color || p.payload?.color || p.fill || brand.primary }} />
          <span>{p.name}: <span className="font-medium text-content">{fmtQty(p.value)}</span>{unit ? ` ${unit}` : ''}</span>
        </div>
      ))}
    </div>
  );
}

function EmptyChart({ text }: { text: string }) {
  return <div className="flex h-[196px] items-center justify-center px-4 text-center text-sm text-subtle">{text}</div>;
}
function ListSkeleton() {
  return <div className="space-y-2 py-1">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>;
}
function Empty({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-subtle">
      <span className="opacity-60">{icon}</span>
      <span className="text-sm">{text}</span>
    </div>
  );
}
